import React from 'react';
import { PERMISSIONS, DEFAULT_KEY_PERMISSIONS, grantRate } from './permissionMeta';

/**
 * Summary-before-detail grant-rate panels — one per platform (Android / iOS).
 * Each shows the platform's user count and, for the selected key permissions,
 * the granted count + grant-rate % (of that platform's users). Divide-by-zero
 * on a zero-user platform reads 0% (never NaN).
 *
 * Props:
 *   counts   userPermissionCount[0]  ({ <perm>_count, android_<perm>_count, ios_<perm>_count })
 *   summary  usersData[0]            ({ android_users, ios_users, android_mobile_app_users, … })
 *   selected string[]                selected permission keys (falls back to the key set)
 */
const labelOf = (key) => (PERMISSIONS.find((p) => p.key === key) || {}).label || key;

const Panel = ({ platform, testid, users, mobileUsers, watchUsers, rows }) => (
  <div className="au-panel up-panel" data-testid={testid}>
    <div className="au-panel__head">
      <h4 className="au-panel__title">{platform} · <span className="tnum">{users}</span> users</h4>
      <span className="au-spacer" />
      <span className="up-panel__sub">Mobile <span className="tnum">{mobileUsers ?? 0}</span> · Watch <span className="tnum">{watchUsers ?? 0}</span></span>
    </div>
    <div className="up-grant-grid">
      {rows.map((r) => (
        <div className="up-grant" key={r.key}>
          <div className="up-grant__label">{r.label}</div>
          <div className="up-grant__value">
            <b className="tnum">{r.rate}%</b>
            <em><span className="tnum">{r.count}</span> granted</em>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const PlatformGrantPanels = ({ counts, summary, selected }) => {
  const c = counts || {};
  const s = summary || {};
  const keys = selected && selected.length ? selected : DEFAULT_KEY_PERMISSIONS;

  const rowsFor = (prefix, denom) =>
    keys.map((key) => {
      const count = c[`${prefix}_${key}_count`] ?? 0;
      return { key, label: labelOf(key), count, rate: grantRate(count, denom) };
    });

  return (
    <div className="up-panels">
      <Panel
        platform="Android"
        testid="up-panel-android"
        users={s.android_users ?? 0}
        mobileUsers={s.android_mobile_app_users}
        watchUsers={s.android_watch_app_users}
        rows={rowsFor('android', s.android_users ?? 0)}
      />
      <Panel
        platform="iOS"
        testid="up-panel-ios"
        users={s.ios_users ?? 0}
        mobileUsers={s.ios_mobile_app_users}
        watchUsers={s.ios_watch_app_users}
        rows={rowsFor('ios', s.ios_users ?? 0)}
      />
    </div>
  );
};

export default PlatformGrantPanels;
